"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import {
  MessageCircle,
  MessageSquareHeart,
  X,
  Send,
  Loader2,
  Camera,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  ZoomIn,
  Upload,
  Paperclip,
  ListTodo,
  Plus,
  PenLine,
  Pencil,
  Check,
  Trash2,
  Sparkles,
  Maximize2,
  Minimize2,
  Mail,
  User,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { fetchApi } from "@/lib/api/client";
import { useAuthStore } from "@/stores/auth-store";
import { ScreenshotAnnotator } from "@/components/feedback/screenshot-annotator";
import {
  statusConfig,
  categoryLabels,
  FEEDBACK_USER_ID_KEY,
  FEEDBACK_USER_EMAIL_KEY,
  FEEDBACK_USER_NAME_KEY,
  FEEDBACK_SUBMITTER_CONFIRMED_KEY,
  MAX_FEEDBACK_LENGTH,
} from "@/lib/feedback/config";
import { useFabCorner, fabPositionClasses, type FabCorner } from "@/hooks/use-fab-corner";
import { useFabAutoCorner } from "@/hooks/use-fab-auto-corner";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/lib/i18n";

// ── Bug #18: local i18n fallback map ───────────────────────────────
// Wraps the ~10 most-visible strings. If the locale file ships a key
// under feedbackWidget.<key>, it takes precedence; otherwise we fall
// back to this English map so no string goes raw-untranslated.
// TODO(#18-full-i18n): port long body strings (AI analysis prompts,
// confirmation paragraphs, etc.) once the messages/*.json files have
// the matching keys.
const FEEDBACK_WIDGET_FALLBACK: Record<string, string> = {
  submitFeedback: "Submit Feedback",
  close: "Close",
  minimize: "Minimize",
  maximize: "Maximize",
  expand: "Expand",
  myTickets: "My tickets",
  myTicketsHeader: "My Tickets",
  newFeedback: "New Feedback",
  viewMyTickets: "View My Tickets",
  send: "Send",
  thankYou: "The admin team will review and take action. Thank you!",
  noTicketsYet: "No tickets yet",
  closePreview: "Close preview",
  dropScreenshotHere: "Drop screenshot here",
};

function useFeedbackWidgetT() {
  const { t } = useTranslation();
  return (key: keyof typeof FEEDBACK_WIDGET_FALLBACK): string => {
    const translated = t(`feedbackWidget.${key}`);
    // useTranslation.t() returns the key unchanged when missing, so
    // detect that and fall back to the English string.
    if (translated === `feedbackWidget.${key}`) {
      return FEEDBACK_WIDGET_FALLBACK[key];
    }
    return translated;
  };
}

type ChatStep =
  | "idle"
  | "identity"
  | "writing"
  | "capturing"
  | "summarizing"
  | "confirming"
  | "submitting"
  | "done";
type WidgetView = "chat" | "my-tickets";

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  content: string;
  type?: "text" | "screenshot" | "tldr";
  screenshotUrl?: string;
}

interface ThreadMessage {
  id: string;
  role: "admin" | "user";
  senderName: string;
  content: string;
  timestamp: string;
}

interface MyTicket {
  id: string;
  tldr: string;
  userStory?: string | null;
  acceptanceCriteria?: string[] | null;
  category: string;
  priority: string;
  status: string;
  timestamp?: string;
  createdAt?: string;
  affectedArea: string;
  adminResponse?: string;
  threadFile?: string | null;
  unreadByUser?: number;
}

function getDisplayTicketId(id: string): string {
  const parts = id.split("-");
  return `#${(parts[parts.length - 1] || id.slice(-6)).toUpperCase()}`;
}

// Pre-auth + non-tenant surfaces where FeedbackWidget must NOT render.
// FIX (portal-manager-fix-wave-1, 2026-05-12): replaced the private prefix
// list with the shared constant in lib/routing/hidden-route-prefixes.ts —
// previously this list, ChatCompanion's list, and HeaderControls's list had
// drifted (eg. /teams-portal + /user-portal were missing from all three).
// The shared constant codifies the rule "chrome shows only in the main app,
// never in Admin Portal preview routes, User Portal, or Public Pages."
import { isHiddenChromeRoute } from "@/lib/routing/hidden-route-prefixes";

function getUserIdentity(): { email: string; name: string } | null {
  if (typeof window === "undefined") return null;
  const email = localStorage.getItem(FEEDBACK_USER_EMAIL_KEY);
  if (!email) return null;
  const name =
    localStorage.getItem(FEEDBACK_USER_NAME_KEY) || email.split("@")[0];
  return { email, name };
}

function setUserIdentity(email: string, name: string): void {
  localStorage.setItem(FEEDBACK_USER_EMAIL_KEY, email);
  localStorage.setItem(FEEDBACK_USER_NAME_KEY, name || email.split("@")[0]);
}

function getUserId(): string {
  if (typeof window === "undefined") return "anonymous";
  let userId = localStorage.getItem(FEEDBACK_USER_ID_KEY);
  if (!userId) {
    userId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(FEEDBACK_USER_ID_KEY, userId);
  }
  return userId;
}

export function FeedbackWidget() {
  const tw = useFeedbackWidgetT();
  const fab = useFabCorner();
  // The open chat panel anchors off the EFFECTIVE corner so on create/edit
  // pages it opens from bottom-left (same place the FAB lives after auto-
  // relocate), not on top of the wizard's Next/Submit button.
  const { effectiveCorner } = useFabAutoCorner();
  const pathname = usePathname();
  const authUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Hide on pre-auth + non-tenant surfaces (login, onboarding, admin/portal,
  // player, etc.) — sourced from the shared HIDDEN_ROUTE_PREFIXES module.
  const isOnHiddenRoute = isHiddenChromeRoute(pathname ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [view, setView] = useState<WidgetView>("chat");
  const [step, setStep] = useState<ChatStep>("idle");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [feedback, setFeedback] = useState("");
  // Legacy single-slot aliases kept for the summarise/submit payloads;
  // these are derived from the screenshots[] array dynamically.
  // Do NOT add back the old useState declarations — use screenshots[] instead.
  const [tldrData, setTldrData] = useState<{
    tldr: string;
    userStory?: string;
    acceptanceCriteria?: string[];
    category: string;
    priority: string;
    affectedArea: string;
    screenshotContext?: string;
    aiGenerated: boolean;
  } | null>(null);

  // Editable versions of AI-generated fields for the confirmation step
  const [editableUserStory, setEditableUserStory] = useState("");
  const [editableAcceptanceCriteria, setEditableAcceptanceCriteria] = useState<
    string[]
  >([]);
  const [isEditingStory, setIsEditingStory] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: string; content: string }>
  >([]);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isFullscreenAnnotating, setIsFullscreenAnnotating] = useState(false); // fullscreen annotation mode

  // Multi-screenshot accumulator (Fix 3)
  // Each entry: { id, url, source, capturedAt }
  // 'annotatingId' tracks which screenshot is currently being annotated
  const [screenshots, setScreenshots] = useState<
    { id: string; url: string; source: "auto" | "upload" | "capture"; capturedAt: string }[]
  >([]);
  const [annotatingScreenshotId, setAnnotatingScreenshotId] = useState<string | null>(null);

  // Capture-mode: widget minimised, floating button persists across navigation (Fix 4)
  const [isCaptureMode, setIsCaptureMode] = useState(false);

  // User identity state
  const [identityEmail, setIdentityEmail] = useState("");
  const [identityName, setIdentityName] = useState("");
  const [hasIdentity, setHasIdentity] = useState(false);

  // My Tickets state — paginated; fetchMyTickets(1) resets the list,
  // fetchMyTickets(N) appends. `hasMore` is true while another page exists.
  const MY_TICKETS_PAGE_SIZE = 10;
  const [myTickets, setMyTickets] = useState<MyTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [myTicketsPage, setMyTicketsPage] = useState(1);
  const [myTicketsHasMore, setMyTicketsHasMore] = useState(false);
  const [loadingMoreTickets, setLoadingMoreTickets] = useState(false);

  // Thread state (for expanded ticket in My Tickets)
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Derived: first auto-capture screenshot url (for summarise API compat)
  const screenshot = screenshots.find((s) => s.source === "auto" || s.source === "capture")?.url ?? null;
  // Derived: first upload screenshot url (for summarise API compat)
  const uploadedScreenshot = screenshots.find((s) => s.source === "upload")?.url ?? null;

  useEffect(() => {
    setMounted(true);
    // Pre-fill state from stored identity (if the user has already confirmed it)
    const confirmed =
      typeof window !== "undefined" &&
      !!localStorage.getItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY);
    const identity = getUserIdentity();
    if (confirmed && identity) {
      setIdentityEmail(identity.email);
      setIdentityName(identity.name);
      setHasIdentity(true);
    } else if (isAuthenticated && authUser) {
      // Pre-fill fields but DON'T set hasIdentity — identity step will show pre-filled
      setIdentityEmail(authUser.email);
      setIdentityName(
        [authUser.firstName, authUser.lastName].filter(Boolean).join(" ") ||
          authUser.email.split("@")[0],
      );
    }
  }, [isAuthenticated, authUser]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addMessage = useCallback(
    (
      role: "assistant" | "user",
      content: string,
      extras?: Partial<ChatMessage>,
    ) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          role,
          content,
          ...extras,
        },
      ]);
    },
    [],
  );

  // --- My Tickets ---

  const fetchMyTickets = useCallback(
    async (page: number = 1) => {
      const isFirstPage = page === 1;
      if (isFirstPage) {
        setLoadingTickets(true);
      } else {
        setLoadingMoreTickets(true);
      }
      try {
        // The BE validates submittedById as a UUID — use the real
        // authenticated user's id.
        const submittedById = authUser?.id;
        if (!submittedById) {
          if (isFirstPage) setMyTickets([]);
          setMyTicketsHasMore(false);
          return;
        }
        const url =
          `/feedback?submittedById=${submittedById}` +
          `&page=${page}&limit=${MY_TICKETS_PAGE_SIZE}`;
        const raw = await fetchApi<
          MyTicket[] | { data: MyTicket[]; total?: number; totalPages?: number }
        >(url);
        // Production returns paginated { data, total, totalPages, ... },
        // demo (legacy mock) returns a flat array.
        const tickets = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { data: MyTicket[] }).data)
            ? (raw as { data: MyTicket[] }).data
            : [];
        const totalPages =
          !Array.isArray(raw) && typeof raw.totalPages === "number"
            ? raw.totalPages
            : undefined;

        if (isFirstPage) {
          setMyTickets(tickets);
        } else {
          setMyTickets((prev) => {
            // Dedupe by id in case the BE shifts pages between fetches.
            const seen = new Set(prev.map((t) => t.id));
            const merged = [...prev];
            for (const t of tickets) if (!seen.has(t.id)) merged.push(t);
            return merged;
          });
        }

        // hasMore: prefer BE totalPages signal; fall back to "exact page-size"
        // heuristic for the legacy demo flat-array shape.
        const hasMore =
          totalPages !== undefined
            ? page < totalPages
            : tickets.length === MY_TICKETS_PAGE_SIZE;
        setMyTicketsHasMore(hasMore);
        setMyTicketsPage(page);
      } catch {
        if (isFirstPage) setMyTickets([]);
        setMyTicketsHasMore(false);
      } finally {
        if (isFirstPage) {
          setLoadingTickets(false);
        } else {
          setLoadingMoreTickets(false);
        }
      }
    },
    [authUser],
  );

  // --- Thread functions ---

  const fetchThread = useCallback(async (ticketId: string) => {
    setThreadLoading(true);
    setThreadMessages([]);
    try {
      // GET /feedback/:ticketId/thread — returns ThreadMessage[]
      const data = await fetchApi<ThreadMessage[]>(
        `/feedback/${ticketId}/thread`,
      );
      setThreadMessages(Array.isArray(data) ? data : []);
    } catch {
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const handleSendReply = useCallback(
    async (ticketId: string) => {
      if (!replyText.trim()) return;
      setIsSendingReply(true);
      try {
        // POST /feedback/:ticketId/thread — body { message }
        // Backend determines sender from JWT (submitter = User, else = Admin)
        await fetchApi(`/feedback/${ticketId}/thread`, {
          method: "POST",
          body: JSON.stringify({ message: replyText.trim() }),
        });
        // Re-fetch thread to get updated messages
        const updated = await fetchApi<ThreadMessage[]>(
          `/feedback/${ticketId}/thread`,
        );
        setThreadMessages(Array.isArray(updated) ? updated : []);
        setReplyText("");
      } catch {
        // silent fail
      } finally {
        setIsSendingReply(false);
      }
    },
    [replyText],
  );

  // --- Screenshot Capture ---

  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    setIsCapturing(true);
    try {
      // Wait for drawer/panel/modal animations to fully complete before capturing.
      // Uses a two-phase approach: first wait for any active CSS transitions to settle,
      // then add a safety buffer for complex staggered animations (drawers, sidepanels).
      await new Promise<void>((resolve) => {
        const animatingEl = document.querySelector(
          '[data-state="open"][class*="transition"], [data-state="open"][class*="animate"], .sheet-content, [role="dialog"]'
        );
        if (animatingEl) {
          const onEnd = () => {
            animatingEl.removeEventListener("transitionend", onEnd);
            setTimeout(resolve, 150);
          };
          animatingEl.addEventListener("transitionend", onEnd, { once: true });
          // Fallback if transitionend never fires (e.g. no active transition)
          setTimeout(() => {
            animatingEl.removeEventListener("transitionend", onEnd);
            resolve();
          }, 600);
        } else {
          // No animating element found — use a flat delay
          requestAnimationFrame(() => setTimeout(resolve, 500));
        }
      });
      const html2canvas = (await import("html2canvas-pro")).default;
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        logging: false,
        ignoreElements: (el: Element) => el.id === "feedback-widget-container",
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
        onclone: (_doc: Document) => {
          // Strip CSS animations so html2canvas reads final-state styles instead of
          // animation-start values. Without this, Radix UI portals (sheets, drawers)
          // with slide-in/fade-in animations render off-screen or invisible.
          const style = _doc.createElement("style");
          style.textContent = "*, *::before, *::after { animation: none !important; transition: none !important; }";
          _doc.head.appendChild(style);
        },
      });

      const dataUrl = canvas.toDataURL("image/png", 0.8);
      setScreenshots((prev) => [
        ...prev,
        {
          id: `ss-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          url: dataUrl,
          source: "auto",
          capturedAt: new Date().toISOString(),
        },
      ]);
      return dataUrl;
    } catch (err) {
      console.error("Screenshot capture failed:", err);
      return null;
    } finally {
      setIsCapturing(false);
    }
  }, []);

  // --- Image Upload Helpers ---

  const processImageFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) {
        addMessage(
          "assistant",
          "That image is too large (max 5MB). Please use a smaller screenshot.",
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        if (dataUrl) {
          const newId = `ss-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setScreenshots((prev) => [
            ...prev,
            { id: newId, url: dataUrl, source: "upload", capturedAt: new Date().toISOString() },
          ]);
          addMessage("user", "Uploaded a screenshot");
          addMessage("assistant", "Got your uploaded screenshot:", {
            type: "screenshot",
            screenshotUrl: dataUrl,
          });
          addMessage("assistant", "You can continue describing your feedback.");
        }
      };
      reader.readAsDataURL(file);
    },
    [addMessage],
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processImageFile(file);
      if (e.target) e.target.value = "";
    },
    [processImageFile],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) processImageFile(file);
          return;
        }
      }
    },
    [processImageFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        processImageFile(file);
      }
    },
    [processImageFile],
  );

  // --- Screenshot Annotation ---

  const handleAnnotationDone = useCallback(
    (annotatedUrl: string) => {
      // Replace the URL in the screenshots array for the screenshot being annotated
      if (annotatingScreenshotId) {
        setScreenshots((prev) =>
          prev.map((s) =>
            s.id === annotatingScreenshotId ? { ...s, url: annotatedUrl } : s,
          ),
        );
      }
      setIsAnnotating(false);
      setIsFullscreenAnnotating(false);
      setPreviewImage(null);
      setAnnotatingScreenshotId(null);
      addMessage(
        "assistant",
        "Screenshot annotated! Here's the updated version:",
        { type: "screenshot", screenshotUrl: annotatedUrl },
      );
      addMessage("assistant", "Please continue with your feedback.");
    },
    [addMessage, annotatingScreenshotId],
  );

  const handleFullscreenAnnotationDone = useCallback(
    (annotatedUrl: string) => {
      // Replace the URL in the screenshots array for the screenshot being annotated
      if (annotatingScreenshotId) {
        setScreenshots((prev) =>
          prev.map((s) =>
            s.id === annotatingScreenshotId ? { ...s, url: annotatedUrl } : s,
          ),
        );
      }
      setIsFullscreenAnnotating(false);
      setPreviewImage(null);
      setAnnotatingScreenshotId(null);
      addMessage(
        "assistant",
        "Screenshot annotated in full view! Here's the updated version:",
        { type: "screenshot", screenshotUrl: annotatedUrl },
      );
      addMessage("assistant", "Please continue with your feedback.");
    },
    [addMessage, annotatingScreenshotId],
  );

  // --- Widget Open/Close ---

  const startChatFlow = useCallback(async () => {
    setStep("capturing");
    addMessage("assistant", "Let me take a snapshot of what you're seeing...");

    setTimeout(async () => {
      const shot = await captureScreenshot();
      if (shot) {
        addMessage("assistant", "Got it! Here's what I captured:", {
          type: "screenshot",
          screenshotUrl: shot,
        });
      }
      addMessage(
        "assistant",
        "What's on your mind? Tell me about a feature you'd like, something to change, or an issue you've found.\n\nYou can also paste or drag-drop a screenshot.",
      );
      setStep("writing");
      setTimeout(() => textareaRef.current?.focus(), 100);
    }, 200);
  }, [addMessage, captureScreenshot]);

  const handleIdentitySubmit = useCallback(() => {
    if (!identityEmail.trim()) return;
    const name = identityName.trim() || identityEmail.split("@")[0];
    setUserIdentity(identityEmail.trim(), name);
    setIdentityName(name);
    setHasIdentity(true);
    // Mark that this user has explicitly confirmed who they are
    localStorage.setItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY, "1");
    startChatFlow();
  }, [identityEmail, identityName, startChatFlow]);

  const handleEditIdentity = useCallback(() => {
    // Clear confirmed flag so the identity screen shows again
    localStorage.removeItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY);
    setHasIdentity(false);
    setStep("identity");
    setView("chat");
    setMessages([]);
    setFeedback("");
    setScreenshots([]);
    setAnnotatingScreenshotId(null);
    setTldrData(null);
    setConversationHistory([]);
    setIsAnnotating(false);
    setIsFullscreenAnnotating(false);
    setEditableUserStory("");
    setEditableAcceptanceCriteria([]);
    setIsEditingStory(false);
  }, []);

  const handleOpen = useCallback(async () => {
    // Close AI chat when feedback opens (cross-widget coordination)
    window.dispatchEvent(new CustomEvent("widget:close-chat"));
    // Tell the FAB stack to hide while this panel is visible.
    window.dispatchEvent(new CustomEvent("fab-widget:open", { detail: "feedback" }));
    setIsOpen(true);
    setIsMaximized(false);
    setIsCaptureMode(false);
    setView("chat");
    setMessages([]);
    setFeedback("");
    setScreenshots([]);
    setAnnotatingScreenshotId(null);
    setTldrData(null);
    setConversationHistory([]);
    setIsAnnotating(false);
    setIsFullscreenAnnotating(false);
    setEditableUserStory("");
    setEditableAcceptanceCriteria([]);
    setIsEditingStory(false);
    setExpandedTicketId(null);

    // Only skip identity screen if the user has explicitly confirmed their identity before
    const confirmed =
      typeof window !== "undefined" &&
      !!localStorage.getItem(FEEDBACK_SUBMITTER_CONFIRMED_KEY);
    const identity = getUserIdentity();
    if (confirmed && identity) {
      setIdentityEmail(identity.email);
      setIdentityName(identity.name);
      setHasIdentity(true);
      startChatFlow();
    } else {
      // Show identity step — pre-fill from stored identity or auth store
      if (identity) {
        setIdentityEmail(identity.email);
        setIdentityName(identity.name);
      } else if (isAuthenticated && authUser) {
        setIdentityEmail(authUser.email);
        setIdentityName(
          [authUser.firstName, authUser.lastName].filter(Boolean).join(" ") ||
            authUser.email.split("@")[0],
        );
      }
      setStep("identity");
    }
  }, [startChatFlow, isAuthenticated, authUser]);

  // Poll for unread messages every 30s (only when widget is closed and user is authenticated)
  useEffect(() => {
    if (isOpen || !isAuthenticated || !authUser?.id) return;

    let cancelled = false;
    const poll = async () => {
      try {
        // BE expects a UUID submittedById — use the authenticated user's id.
        const submittedById = authUser.id;
        if (!submittedById) return;
        const raw = await fetchApi<MyTicket[] | { data: MyTicket[] }>(
          `/feedback?submittedById=${submittedById}&limit=50`,
        );
        if (cancelled) return;
        const tickets = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { data: MyTicket[] }).data)
            ? (raw as { data: MyTicket[] }).data
            : [];
        const unreadCount = tickets.reduce(
          (sum, t) => sum + (t.unreadByUser ?? 0),
          0,
        );
        window.dispatchEvent(
          new CustomEvent("feedback:unread-count", { detail: unreadCount }),
        );
      } catch {
        // Silently ignore polling errors
      }
    };

    poll(); // Initial check
    const interval = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen, isAuthenticated, authUser?.id]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsMaximized(false);
    setIsCaptureMode(false);
    setStep("idle");
    setMessages([]);
    setFeedback("");
    setScreenshots([]);
    setAnnotatingScreenshotId(null);
    setTldrData(null);
    setConversationHistory([]);
    setIsAnnotating(false);
    setIsFullscreenAnnotating(false);
    setEditableUserStory("");
    setEditableAcceptanceCriteria([]);
    setIsEditingStory(false);
    // Re-show the FAB stack once the panel is gone.
    window.dispatchEvent(new CustomEvent("fab-widget:close", { detail: "feedback" }));
  }, []);

  // Close feedback when AI chat opens (cross-widget coordination)
  useEffect(() => {
    const handler = () => {
      if (isOpen) handleClose();
    };
    window.addEventListener("widget:close-feedback", handler);
    return () => window.removeEventListener("widget:close-feedback", handler);
  }, [isOpen, handleClose]);

  // Close on Escape key (capture phase so it fires before any stopPropagation)
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (previewImage) {
          setPreviewImage(null);
        } else if (isAnnotating) {
          setIsAnnotating(false);
        } else {
          handleClose();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, handleClose, previewImage, isAnnotating]);

  // --- Multi-turn AI Chat ---

  const handleSendFeedback = useCallback(async () => {
    if (!feedback.trim()) return;

    const userFeedback = feedback.trim();
    setFeedback("");
    addMessage("user", userFeedback);

    const updatedHistory = [
      ...conversationHistory,
      { role: "user", content: userFeedback },
    ];
    setConversationHistory(updatedHistory);

    setStep("summarizing");
    addMessage("assistant", "Let me understand what you're saying...");

    try {
      const data = await fetchApi<{
        type?: "clarification";
        question?: string;
        tldr?: string;
        userStory?: string;
        acceptanceCriteria?: string[];
        category?: string;
        priority?: string;
        affectedArea?: string;
        screenshotContext?: string;
        aiGenerated?: boolean;
      }>("/feedback/summarize", {
        method: "POST",
        body: JSON.stringify({
          feedback: userFeedback,
          pathname: window.location.pathname,
          conversationHistory: updatedHistory,
          // `screenshot` + `uploadedScreenshot` remain for back-compat
          // (older FE deploys, custom integrations) — they are the first
          // auto-capture and the first upload. `additionalScreenshots`
          // carries every OTHER attached image so the LLM can consider
          // the full visual story (e.g. the user captured 3 pages via
          // "Add another screenshot from another page" — pre-fix only
          // the first reached the model). BE de-dupes + caps server-side.
          screenshot: screenshot,
          uploadedScreenshot: uploadedScreenshot,
          additionalScreenshots: screenshots
            .map((s) => s.url)
            .filter(
              (url) => url && url !== screenshot && url !== uploadedScreenshot,
            ),
        }),
      });

      // AI wants to ask a clarifying question
      if (data.type === "clarification") {
        const aiQuestion = data.question ?? "";
        setConversationHistory((prev) => [
          ...prev,
          { role: "assistant", content: aiQuestion },
        ]);
        addMessage("assistant", aiQuestion);
        setStep("writing");
        setTimeout(() => textareaRef.current?.focus(), 100);
        return;
      }

      // AI produced a summary with user story
      setTldrData({
        tldr: data.tldr ?? "",
        userStory: data.userStory,
        acceptanceCriteria: data.acceptanceCriteria,
        category: data.category ?? "General",
        priority: data.priority ?? "Medium",
        affectedArea: data.affectedArea ?? "Unknown",
        screenshotContext: data.screenshotContext,
        aiGenerated: data.aiGenerated ?? true,
      });
      setEditableUserStory(data.userStory ?? data.tldr ?? "");
      setEditableAcceptanceCriteria(
        Array.isArray(data.acceptanceCriteria) ? data.acceptanceCriteria : [],
      );
      setIsEditingStory(false);

      addMessage(
        "assistant",
        "I've prepared a summary of your feedback. Please review and confirm below.",
        { type: "tldr" },
      );
      setStep("confirming");
    } catch {
      const fallback = {
        tldr: userFeedback.slice(0, 200),
        category: "General",
        priority: "Medium",
        affectedArea: "Unknown",
        aiGenerated: false,
      };
      setTldrData(fallback);
      addMessage(
        "assistant",
        `I'll submit your feedback as-is:\n\n"${userFeedback.slice(0, 200)}"\n\nShall I go ahead and create this ticket?`,
        { type: "tldr" },
      );
      setStep("confirming");
    }
  }, [
    feedback,
    addMessage,
    conversationHistory,
    screenshot,
    uploadedScreenshot,
    screenshots,
    // screenshot and uploadedScreenshot are derived from screenshots[]
  ]);

  // --- Ticket Submission ---

  const handleConfirm = useCallback(async () => {
    setStep("submitting");
    addMessage("assistant", "Submitting your ticket to the admin team...");

    try {
      const ticket = await fetchApi<{ id: string }>("/feedback", {
        method: "POST",
        body: JSON.stringify({
          feedback: messages
            .filter((m) => m.role === "user")
            .map((m) => m.content)
            .join("\n\n"),
          url: window.location.href,
          pathname: window.location.pathname,
          userAgent: navigator.userAgent,
          screenResolution: `${window.innerWidth}x${window.innerHeight}`,
          submitterName: identityName || undefined,
          submitterEmail: identityEmail || undefined,
          tldr: tldrData?.tldr,
          userStory: editableUserStory || tldrData?.userStory,
          acceptanceCriteria:
            editableAcceptanceCriteria.length > 0
              ? editableAcceptanceCriteria
              : tldrData?.acceptanceCriteria,
          category: tldrData?.category,
          priority: tldrData?.priority,
          affectedArea: tldrData?.affectedArea,
          screenshotContext: tldrData?.screenshotContext,
          aiGenerated: tldrData?.aiGenerated,
        }),
      });

      // Upload all accumulated screenshots to file storage (non-blocking)
      if (ticket?.id && screenshots.length > 0) {
        const uploads: Promise<unknown>[] = screenshots.map((ss) =>
          fetchApi(`/feedback/${ticket.id}/screenshots/upload`, {
            method: "POST",
            body: JSON.stringify({
              dataUrl: ss.url,
              type: ss.source === "upload" ? "Uploaded" : "Auto",
            }),
          }).catch(() => {
            /* silent — screenshot upload is best-effort */
          }),
        );
        await Promise.all(uploads);
      }

      // Show what was submitted so the user has a record
      const submittedStory = editableUserStory || tldrData?.userStory;
      const submittedCriteria =
        editableAcceptanceCriteria.length > 0
          ? editableAcceptanceCriteria
          : tldrData?.acceptanceCriteria;

      let confirmationMsg =
        "Your ticket has been submitted! Here's what was recorded:\n\n";
      if (tldrData?.tldr) {
        confirmationMsg += `**Summary:** ${tldrData.tldr}\n\n`;
      }
      if (submittedStory) {
        confirmationMsg += `**User Story:**\n"${submittedStory}"\n\n`;
      }
      if (submittedCriteria && submittedCriteria.length > 0) {
        confirmationMsg += `**Acceptance Criteria:**\n${submittedCriteria.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\n`;
      }
      confirmationMsg += tw("thankYou");

      addMessage("assistant", confirmationMsg);
      setStep("done");
    } catch {
      addMessage("assistant", "Sorry, something went wrong. Please try again.");
      setStep("writing");
    }
  }, [
    addMessage,
    screenshots,
    tldrData,
    messages,
    editableUserStory,
    editableAcceptanceCriteria,
    identityName,
    identityEmail,
  ]);

  const handleReject = useCallback(() => {
    addMessage("user", "No, let me clarify.");
    addMessage(
      "assistant",
      "No problem! Please add more details or rephrase what you'd like.",
    );
    setStep("writing");
    setTldrData(null);
    setEditableUserStory("");
    setEditableAcceptanceCriteria([]);
    setIsEditingStory(false);
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [addMessage]);

  // --- Retake Screenshot ---

  const handleRetakeScreenshot = useCallback(async () => {
    if (isCapturing) return;
    setStep("capturing");
    addMessage("user", "Retake screenshot");
    addMessage("assistant", "Taking a new snapshot...");

    await new Promise((resolve) => setTimeout(resolve, 300));

    const shot = await captureScreenshot();
    if (shot) {
      addMessage("assistant", "Updated! Here's the new screenshot:", {
        type: "screenshot",
        screenshotUrl: shot,
      });
    } else {
      addMessage(
        "assistant",
        "Screenshot capture failed. You can try again or upload one manually.",
      );
    }
    addMessage("assistant", "Please continue with your feedback.");
    setStep("writing");
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [addMessage, captureScreenshot, isCapturing]);

  // Fix 4 — Enter capture mode: minimise widget, show floating button
  const handleEnterCaptureMode = useCallback(() => {
    setIsOpen(false); // visually hide the sheet but keep all state
    setIsCaptureMode(true);
  }, []);

  // Fix 4 — Capture screenshot from another page and re-open widget
  const handleCaptureModeCapture = useCallback(async () => {
    const shot = await captureScreenshot();
    if (shot) {
      // Override the auto-type entry that captureScreenshot() just pushed
      // with source='capture' so it's distinguishable
      setScreenshots((prev) => {
        const last = [...prev];
        for (let i = last.length - 1; i >= 0; i--) {
          if (last[i].source === "auto") {
            last[i] = { ...last[i], source: "capture" };
            break;
          }
        }
        return last;
      });
      addMessage("assistant", "Captured another page! Here's the new screenshot:", {
        type: "screenshot",
        screenshotUrl: shot,
      });
      addMessage("assistant", "You can capture more pages or continue with your feedback.");
    }
    setIsCaptureMode(false);
    setIsOpen(true);
  }, [captureScreenshot, addMessage]);

  const handleCancelCaptureMode = useCallback(() => {
    setIsCaptureMode(false);
    setIsOpen(true);
  }, []);

  if (!mounted) return null;

  // Don't render on pre-auth or non-tenant surfaces — mirrors ChatCompanion.
  if (isOnHiddenRoute) return null;

  return createPortal(
    <div
      id="feedback-widget-container"
      data-slot="feedback-widget"
      className={cn(
        // FIX: 1d7d4e92 / 2ee781d8 — lower from z-[9999] to z-40 so the
        // wizard footer (z-50) sits above the FAB. z-[9999] caused the
        // feedback heart to intercept clicks on the wizard Next button
        // (same bottom-right area), making the widget appear to auto-open
        // on every Next press.
        "fixed z-40 transition-[bottom,left,right,top] duration-500 ease-[cubic-bezier(.16,1,.3,1)] motion-reduce:transition-none",
        isOpen ? fabPositionClasses(effectiveCorner, "primary") : "bottom-6 right-6 pointer-events-none",
      )}
      style={{ pointerEvents: isOpen ? "auto" : undefined }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hidden file input for screenshot upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Draggable FAB — independent trigger for feedback */}
      {!isOpen && (
        <FeedbackFab onOpen={handleOpen} unreadCount={0} />
      )}

      {/* Maximized backdrop */}
      {isOpen && isMaximized && (
        <div className="fixed inset-0 z-[9998] bg-background/80 backdrop-blur-sm" />
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className={cn(
            "flex flex-col rounded-2xl border bg-background shadow-2xl overflow-hidden",
            "animate-in fade-in-0 zoom-in-95 duration-200",
            isMaximized
              ? "fixed inset-4 z-[9999]"
              : cn(
                  "absolute w-[calc(100vw-2rem)] sm:w-[400px] h-[calc(100vh-6rem)] sm:h-[520px] max-w-[400px] max-h-[520px]",
                  effectiveCorner.startsWith("bottom")
                    ? "bottom-14 slide-in-from-bottom-2"
                    : "top-14 slide-in-from-top-2",
                  effectiveCorner.endsWith("right") ? "right-0" : "left-0",
                ),
          )}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              <div>
                <h3 className="text-sm font-semibold">Feedback</h3>
                {hasIdentity ? (
                  <button
                    onClick={handleEditIdentity}
                    title="Change your identity"
                    className="text-[10px] opacity-80 flex items-center gap-1 hover:opacity-100 transition-opacity"
                  >
                    <Mail className="h-2.5 w-2.5" /> {identityEmail}{" "}
                    <Pencil className="h-2 w-2" />
                  </button>
                ) : (
                  <p className="text-[10px] opacity-80">
                    Help us improve the platform
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  if (view === "my-tickets") {
                    setView("chat");
                  } else {
                    setView("my-tickets");
                    fetchMyTickets();
                  }
                }}
                className={cn(
                  "rounded-md p-1.5 transition-colors",
                  view === "my-tickets" ? "bg-white/20" : "hover:bg-white/20",
                )}
                aria-label={tw("myTickets")}
                title={tw("myTickets")}
              >
                <ListTodo className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsMaximized(!isMaximized)}
                className="rounded-md p-1.5 hover:bg-white/20 transition-colors"
                aria-label={isMaximized ? tw("minimize") : tw("maximize")}
                title={isMaximized ? tw("minimize") : tw("expand")}
              >
                {isMaximized ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={handleClose}
                className="rounded-md p-1 hover:bg-white/20 transition-colors"
                aria-label={tw("close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Drag overlay */}
          {isDragging && view === "chat" && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-2xl m-1">
              <div className="flex flex-col items-center gap-2 text-primary">
                <Upload className="h-8 w-8" />
                <p className="text-sm font-medium">{tw("dropScreenshotHere")}</p>
              </div>
            </div>
          )}

          {/* === MY TICKETS VIEW === */}
          {view === "my-tickets" && (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h4 className="text-sm font-semibold">{tw("myTicketsHeader")}</h4>
                <button
                  onClick={() => {
                    setView("chat");
                    if (step === "idle") handleOpen();
                  }}
                  className="text-xs text-primary flex items-center gap-1 hover:underline"
                >
                  <Plus className="h-3 w-3" /> {tw("newFeedback")}
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {loadingTickets ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : myTickets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <ListTodo className="h-10 w-10 text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {tw("noTicketsYet")}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      Your submitted feedback will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {myTickets.map((ticket) => {
                      const st =
                        statusConfig[ticket.status] || statusConfig["Open"];
                      const isExpanded = expandedTicketId === ticket.id;
                      const hasUnread = (ticket.unreadByUser || 0) > 0;
                      return (
                        <div
                          key={ticket.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <button
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedTicketId(null);
                                setThreadMessages([]);
                                setReplyText("");
                              } else {
                                setExpandedTicketId(ticket.id);
                                fetchThread(ticket.id);
                              }
                            }}
                            className="w-full text-left px-4 py-3"
                          >
                            <div className="flex items-center gap-2">
                              {hasUnread && (
                                <span className="shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                              )}
                              <p
                                className={cn(
                                  "text-sm font-medium leading-snug flex-1",
                                  !isExpanded && "line-clamp-2",
                                )}
                              >
                                {ticket.tldr}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {getDisplayTicketId(ticket.id)}
                              </span>
                              <Badge
                                variant="outline"
                                className={cn("text-[10px] h-5", st.color)}
                              >
                                {st.label}
                              </Badge>
                              <span className="text-[10px] text-muted-foreground">
                                {categoryLabels[ticket.category] ||
                                  ticket.category}
                              </span>
                              <span className="text-[10px] text-muted-foreground ml-auto">
                                {new Date(
                                  ticket.createdAt || ticket.timestamp || "",
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </button>

                          {/* Expanded: conversation thread */}
                          {isExpanded && (
                            <div className="px-4 pb-3 space-y-2.5">
                              {/* Thread messages */}
                              {threadLoading ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                </div>
                              ) : threadMessages.length > 0 ? (
                                <div className="space-y-2 max-h-[200px] overflow-y-auto rounded-lg border bg-muted/20 p-2.5">
                                  {threadMessages.map((msg) => (
                                    <div
                                      key={msg.id}
                                      className={cn(
                                        "flex",
                                        msg.role === "user"
                                          ? "justify-end"
                                          : "justify-start",
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          "max-w-[85%] rounded-xl px-3 py-2 text-[11px] leading-relaxed",
                                          msg.role === "user"
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-blue-50 dark:bg-blue-950/30 text-blue-900 dark:text-blue-200 border border-blue-200 dark:border-blue-800",
                                        )}
                                      >
                                        <p className="font-medium text-[10px] mb-0.5 opacity-70">
                                          {msg.senderName}
                                        </p>
                                        {msg.content}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center py-3 text-center">
                                  <p className="text-[11px] text-muted-foreground">
                                    No messages yet
                                  </p>
                                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                                    Admin replies will appear here
                                  </p>
                                </div>
                              )}

                              {/* Reply compose bar */}
                              <div className="flex items-end gap-2">
                                <Textarea
                                  placeholder="Reply to admin..."
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  className="min-h-[36px] max-h-[80px] resize-none text-xs border bg-muted rounded-xl px-3 py-2 focus-visible:ring-1"
                                  rows={1}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSendReply(ticket.id);
                                    }
                                  }}
                                />
                                <Button
                                  onClick={() => handleSendReply(ticket.id)}
                                  disabled={!replyText.trim() || isSendingReply}
                                  size="icon"
                                  className="rounded-full h-8 w-8 shrink-0"
                                >
                                  {isSendingReply ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Send className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Admin response preview when collapsed */}
                          {!isExpanded && ticket.adminResponse && (
                            <div className="px-4 pb-3">
                              <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 px-2.5 py-1.5 border border-blue-200 dark:border-blue-800">
                                <p className="text-[10px] font-medium text-blue-700 dark:text-blue-400 mb-0.5">
                                  Admin responded:
                                </p>
                                <p className="text-[11px] text-blue-900 dark:text-blue-300 line-clamp-2">
                                  {ticket.adminResponse}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {myTicketsHasMore && (
                      <div className="flex items-center justify-center py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={loadingMoreTickets}
                          onClick={() =>
                            fetchMyTickets(myTicketsPage + 1)
                          }
                          className="text-xs h-8"
                        >
                          {loadingMoreTickets ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                              Loading...
                            </>
                          ) : (
                            "Load more"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === CHAT VIEW === */}
          {view === "chat" && (
            <>
              {/* Identity Form */}
              {step === "identity" && (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 py-8">
                  <div className="w-full max-w-[300px] space-y-5">
                    <div className="text-center space-y-1.5">
                      <div className="mx-auto w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <h4 className="text-sm font-semibold">Who are you?</h4>
                      <p className="text-xs text-muted-foreground">
                        {identityEmail
                          ? "Confirm or update your details"
                          : "So we can follow up on your feedback"}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                          Email address *
                        </label>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          value={identityEmail}
                          onChange={(e) => setIdentityEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleIdentitySubmit();
                          }}
                          className="h-9 text-sm"
                          autoFocus
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                          Display name (optional)
                        </label>
                        <Input
                          type="text"
                          placeholder={
                            identityEmail
                              ? identityEmail.split("@")[0]
                              : "Your name"
                          }
                          value={identityName}
                          onChange={(e) => setIdentityName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleIdentitySubmit();
                          }}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleIdentitySubmit}
                      disabled={!identityEmail.trim()}
                      className="w-full gap-2"
                      size="sm"
                    >
                      Continue <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Chat Messages */}
              {step !== "identity" && (
                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex",
                        msg.role === "user" ? "justify-end" : "justify-start",
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm",
                          msg.role === "user"
                            ? "bg-primary text-primary-foreground rounded-br-md"
                            : "bg-muted text-foreground rounded-bl-md",
                        )}
                      >
                        {msg.type === "screenshot" && msg.screenshotUrl ? (
                          <div className="space-y-2">
                            <p className="text-xs">{msg.content}</p>

                            {/* Inline annotation mode (fallback — normally we jump to fullscreen) */}
                            {isAnnotating &&
                            annotatingScreenshotId !== null &&
                            screenshots.find((s) => s.id === annotatingScreenshotId)?.url === msg.screenshotUrl ? (
                              <ScreenshotAnnotator
                                imageUrl={msg.screenshotUrl}
                                onDone={handleAnnotationDone}
                                onCancel={() => { setIsAnnotating(false); setAnnotatingScreenshotId(null); }}
                              />
                            ) : (
                              <>
                                <div className="relative group/img">
                                  <img
                                    src={msg.screenshotUrl}
                                    alt="Page screenshot"
                                    className="w-full h-[100px] object-cover object-top rounded-lg border border-white/20 cursor-pointer"
                                    onClick={() =>
                                      setPreviewImage(msg.screenshotUrl || null)
                                    }
                                  />
                                  <div
                                    className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover/img:opacity-100 transition-opacity rounded-lg cursor-pointer"
                                    onClick={() =>
                                      setPreviewImage(msg.screenshotUrl || null)
                                    }
                                  >
                                    <ZoomIn className="h-5 w-5 text-white" />
                                  </div>
                                </div>
                                {/* Fix 1+2: single-click → fullscreen annotator with pen active; Button sizes to content */}
                                <div className="flex items-center gap-1 flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs gap-1"
                                    onClick={handleRetakeScreenshot}
                                    disabled={isCapturing}
                                  >
                                    <RotateCcw className="h-3 w-3" /> Retake
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs gap-1"
                                    onClick={() => fileInputRef.current?.click()}
                                  >
                                    <Upload className="h-3 w-3" /> Upload
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs gap-1"
                                    onClick={() => {
                                      // Path A: open lightbox only — let the user click "Annotate Full View"
                                      // inside it once the image is fully rendered (mirrors Path B).
                                      const found = screenshots.find((s) => s.url === msg.screenshotUrl);
                                      if (found) setAnnotatingScreenshotId(found.id);
                                      setPreviewImage(msg.screenshotUrl || null);
                                      // Do NOT set isFullscreenAnnotating here — the image hasn't
                                      // loaded into the annotator yet.  The lightbox's own
                                      // "Annotate Full View" button triggers the transition once
                                      // the <img> is already visible.
                                    }}
                                  >
                                    <PenLine className="h-3 w-3" /> Annotate
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap leading-relaxed">
                            {msg.content
                              .split(/(\*\*[^*]+\*\*)/)
                              .map((part, i) =>
                                part.startsWith("**") && part.endsWith("**") ? (
                                  <strong key={i}>{part.slice(2, -2)}</strong>
                                ) : (
                                  <span key={i}>{part}</span>
                                ),
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Fix 4 — "Add another screenshot from another page" button */}
                  {step === "writing" && screenshots.length > 0 && (
                    <div className="flex justify-start">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1.5"
                        onClick={handleEnterCaptureMode}
                      >
                        <Camera className="h-3 w-3" />
                        Add another screenshot
                      </Button>
                    </div>
                  )}

                  {/* Loading indicator */}
                  {(step === "capturing" ||
                    step === "summarizing" ||
                    step === "submitting") && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {step === "capturing" && "Capturing page..."}
                            {step === "summarizing" &&
                              "Understanding your feedback..."}
                            {step === "submitting" && "Submitting ticket..."}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Done indicator with manual close */}
                  {step === "done" && (
                    <div className="flex flex-col items-center gap-3 py-3">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          Ticket submitted!
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setView("my-tickets");
                            fetchMyTickets();
                          }}
                          className="text-xs text-primary hover:underline font-medium"
                        >
                          {tw("viewMyTickets")}
                        </button>
                        <span className="text-muted-foreground text-xs">·</span>
                        <button
                          onClick={handleClose}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          {tw("close")}
                        </button>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>
              )}

              {/* Confirmation Panel — Editable User Story + Acceptance Criteria */}
              {step === "confirming" && tldrData && (
                <div className="border-t flex flex-col max-h-[55%] overflow-y-auto">
                  {/* Header */}
                  <div className="px-4 py-2.5 bg-muted/40 border-b flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                    <span className="text-xs font-semibold">
                      Review Before Submitting
                    </span>
                  </div>

                  <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1">
                    {/* Metadata badges */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 capitalize"
                      >
                        {tldrData.category?.replace("_", " ")}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] h-5",
                          tldrData.priority === "high"
                            ? "border-red-300 text-red-700"
                            : tldrData.priority === "low"
                              ? "border-green-300 text-green-700"
                              : "border-yellow-300 text-yellow-700",
                        )}
                      >
                        {tldrData.priority}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] h-5">
                        {tldrData.affectedArea}
                      </Badge>
                    </div>

                    {/* TLDR */}
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Summary
                      </p>
                      <p className="text-xs leading-relaxed bg-muted/40 rounded-md px-2.5 py-2">
                        {tldrData.tldr}
                      </p>
                    </div>

                    {/* Editable User Story */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          User Story
                        </p>
                        <button
                          onClick={() => setIsEditingStory(!isEditingStory)}
                          className="text-[10px] text-primary flex items-center gap-0.5 hover:underline"
                        >
                          {isEditingStory ? (
                            <>
                              <Check className="h-2.5 w-2.5" /> Done
                            </>
                          ) : (
                            <>
                              <Pencil className="h-2.5 w-2.5" /> Edit
                            </>
                          )}
                        </button>
                      </div>
                      {isEditingStory ? (
                        <Textarea
                          value={editableUserStory}
                          onChange={(e) => setEditableUserStory(e.target.value)}
                          className="text-xs min-h-[60px] max-h-[100px] resize-none bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/50"
                          rows={3}
                        />
                      ) : (
                        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/50 rounded-md px-2.5 py-2 text-xs leading-relaxed italic text-blue-900 dark:text-blue-200">
                          &ldquo;{editableUserStory}&rdquo;
                        </div>
                      )}
                    </div>

                    {/* Editable Acceptance Criteria */}
                    {editableAcceptanceCriteria.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                          Acceptance Criteria
                        </p>
                        <div className="space-y-1.5">
                          {editableAcceptanceCriteria.map((criterion, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2 group"
                            >
                              <Check className="h-3 w-3 text-green-500 shrink-0 mt-0.5" />
                              {isEditingStory ? (
                                <div className="flex-1 flex items-start gap-1">
                                  <input
                                    type="text"
                                    value={criterion}
                                    onChange={(e) => {
                                      const updated = [
                                        ...editableAcceptanceCriteria,
                                      ];
                                      updated[i] = e.target.value;
                                      setEditableAcceptanceCriteria(updated);
                                    }}
                                    className="flex-1 text-[11px] bg-muted/40 border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                                  />
                                  <button
                                    onClick={() =>
                                      setEditableAcceptanceCriteria(
                                        editableAcceptanceCriteria.filter(
                                          (_, idx) => idx !== i,
                                        ),
                                      )
                                    }
                                    className="shrink-0 p-0.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <p className="text-[11px] leading-relaxed text-muted-foreground">
                                  {criterion}
                                </p>
                              )}
                            </div>
                          ))}
                          {isEditingStory && (
                            <button
                              onClick={() =>
                                setEditableAcceptanceCriteria([
                                  ...editableAcceptanceCriteria,
                                  "",
                                ])
                              }
                              className="text-[10px] text-primary flex items-center gap-1 hover:underline ml-5"
                            >
                              <Plus className="h-2.5 w-2.5" /> Add criterion
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Screenshot context */}
                    {tldrData.screenshotContext && (
                      <p className="text-[10px] text-muted-foreground italic">
                        AI observed: {tldrData.screenshotContext}
                      </p>
                    )}
                  </div>

                  {/* Action buttons — fixed at bottom */}
                  <div className="border-t px-4 py-2.5 flex items-center gap-2 bg-background">
                    <Button
                      onClick={handleConfirm}
                      size="sm"
                      className="flex-1 gap-1.5"
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                      Submit Ticket
                    </Button>
                    <Button
                      onClick={handleReject}
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5"
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                      Let me clarify
                    </Button>
                  </div>
                </div>
              )}

              {/* Input Area */}
              {step === "writing" && (
                <div className="border-t px-3 py-2.5">
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="shrink-0 rounded-full h-9 w-9 flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                      title="Upload screenshot"
                    >
                      <Paperclip className="h-4 w-4" />
                    </button>
                    <Textarea
                      ref={textareaRef}
                      placeholder="Describe your feedback..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      className="min-h-[44px] max-h-[100px] resize-none text-sm border-0 bg-muted rounded-xl px-3 py-2.5 focus-visible:ring-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendFeedback();
                        }
                      }}
                      rows={1}
                    />
                    <Button
                      onClick={handleSendFeedback}
                      disabled={!feedback.trim()}
                      size="icon"
                      className="rounded-full h-9 w-9 shrink-0"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between mt-1 px-1">
                    <p className="text-[10px] text-muted-foreground">
                      Enter to send · Paste or drag an image to attach
                    </p>
                    <p
                      className={cn(
                        "text-[10px]",
                        feedback.length > MAX_FEEDBACK_LENGTH
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}
                    >
                      {feedback.length > 0 &&
                        `${feedback.length.toLocaleString()}/${MAX_FEEDBACK_LENGTH.toLocaleString()}`}
                    </p>
                  </div>
                </div>
              )}

              {/* Page context bar */}
              {step !== "done" && step !== "identity" && (
                <div className="border-t bg-muted/30 px-4 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <Camera className="h-3 w-3 text-muted-foreground" />
                    <p className="text-[10px] text-muted-foreground truncate">
                      {typeof window !== "undefined"
                        ? window.location.pathname
                        : ""}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Screenshot Lightbox with Fullscreen Annotation */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in-0 duration-150"
          onClick={() => {
            if (!isFullscreenAnnotating) {
              setPreviewImage(null);
              setIsFullscreenAnnotating(false);
            }
          }}
        >
          {/* Close button — z-20 keeps it above the annotator toolbar */}
          <button
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors z-20"
            onClick={() => {
              setPreviewImage(null);
              setIsFullscreenAnnotating(false);
            }}
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>

          {isFullscreenAnnotating ? (
            /* Fullscreen annotation mode */
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex flex-col items-center"
            >
              <ScreenshotAnnotator
                imageUrl={previewImage}
                onDone={handleFullscreenAnnotationDone}
                onCancel={() => setIsFullscreenAnnotating(false)}
                fullscreen
              />
            </div>
          ) : (
            /* Normal preview with annotate button */
            <>
              <img
                src={previewImage}
                alt="Screenshot preview"
                className="max-w-[90vw] max-h-[80vh] rounded-lg shadow-2xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              {/* Annotate button below the image */}
              <div
                className="mt-4 flex items-center gap-3"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    // Set annotatingScreenshotId to the matching screenshot entry
                    const found = screenshots.find((s) => s.url === previewImage);
                    if (found) setAnnotatingScreenshotId(found.id);
                    setIsFullscreenAnnotating(true);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-white/10 border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
                >
                  <PenLine className="h-4 w-4" /> Annotate Full View
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Fix 4 — Floating "Capture this page" button in capture mode */}
      {isCaptureMode && createPortal(
        <div className="fixed bottom-6 right-6 z-[10001] flex flex-col items-end gap-2 animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
          <div className="flex items-center gap-2 rounded-2xl bg-background border shadow-2xl px-4 py-2.5">
            <Camera className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Navigate to the page you want to capture</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 shadow-lg"
              onClick={handleCancelCaptureMode}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button
              size="sm"
              className="gap-1.5 shadow-lg"
              onClick={handleCaptureModeCapture}
              disabled={isCapturing}
            >
              {isCapturing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
              Capture this page
            </Button>
          </div>
        </div>,
        document.body,
      )}
    </div>,
    document.body,
  );
}

// ─── Corner-snap Feedback FAB (primary position — closer to edge) ────────────
function FeedbackFab({ onOpen, unreadCount }: { onOpen: () => void; unreadCount: number }) {
  const tw = useFeedbackWidgetT()
  const fab = useFabCorner()
  // Chevron handles + anchor edges follow the EFFECTIVE corner so they
  // line up with the host's animated position, not the operator's saved
  // preference. Saved preference still drives `fab.setCorner` writes.
  const { effectiveCorner } = useFabAutoCorner()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [mounted, setMounted] = useState(false)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const [handlesVisible, setHandlesVisible] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showHandles = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
    setHandlesVisible(true)
  }
  const scheduleHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => setHandlesVisible(false), 400)
  }
  useEffect(() => {
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current) }
  }, [])

  const [badge, setBadge] = useState(unreadCount)
  useEffect(() => {
    const handler = (e: Event) => setBadge((e as CustomEvent).detail || 0)
    window.addEventListener("feedback:unread-count", handler)
    return () => window.removeEventListener("feedback:unread-count", handler)
  }, [])

  // Resolve the portal target (created by FabStackHost). Retry one tick if the
  // host mounts after us on a given route.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setMounted(true)
    const lookup = () => setHost(document.getElementById("fab-stack-root"))
    lookup()
    if (!document.getElementById("fab-stack-root")) {
      const t = setTimeout(lookup, 0)
      return () => clearTimeout(t)
    }
  }, [])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleClick = () => {
    if (!fab.wasDrag()) onOpen()
  }

  if (!isAuthenticated) return null
  if (!mounted || !host) return null

  // Anchor: Heart sits AT the corner edge (no translate). The two
  // directional chevron handles peek out 50px on the perpendicular axes.
  const anchorMap: Record<FabCorner, string> = {
    "top-left":     "top-0 left-0",
    "top-right":    "top-0 right-0",
    "bottom-left":  "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  }
  const anchorEdge = anchorMap[effectiveCorner]

  const isBottom = effectiveCorner.startsWith("bottom")
  const isRight = effectiveCorner.endsWith("right")

  // Vertical handle snaps the FAB to the OPPOSITE vertical corner on the
  // same horizontal side; horizontal handle snaps to the OPPOSITE horizontal
  // corner on the same vertical side.
  const verticalTarget: FabCorner = isBottom
    ? (isRight ? "top-right" : "top-left")
    : (isRight ? "bottom-right" : "bottom-left")
  const horizontalTarget: FabCorner = isRight
    ? (isBottom ? "bottom-left" : "top-left")
    : (isBottom ? "bottom-right" : "top-right")

  const VerticalIcon = isBottom ? ChevronUp : ChevronDown
  const HorizontalIcon = isRight ? ChevronLeft : ChevronRight

  // Vertical handle: 60px above (bottom corner) / below (top corner) Heart.
  const verticalOffset = isBottom ? "-translate-y-[60px]" : "translate-y-[60px]"
  // Horizontal handle: 60px to the LEFT (right corner) / RIGHT (left corner) of Heart.
  const horizontalOffset = isRight ? "-translate-x-[60px]" : "translate-x-[60px]"

  const verticalLabels: Record<FabCorner, string> = {
    "top-left": "top left",
    "top-right": "top right",
    "bottom-left": "bottom left",
    "bottom-right": "bottom right",
  }

  return createPortal(
    <>
      {!fab.isDragging && (
        <>
          <button
            type="button"
            aria-label={`Move feedback to ${verticalLabels[verticalTarget]}`}
            onPointerEnter={showHandles}
            onPointerLeave={scheduleHide}
            onClick={(e) => {
              e.stopPropagation()
              fab.setCorner(verticalTarget)
            }}
            className={cn(
              "absolute w-6 h-6 rounded-full flex items-center justify-center z-10",
              anchorEdge,
              verticalOffset,
              "bg-background text-foreground",
              "border-2 border-foreground transition-opacity duration-200",
              handlesVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            )}
            style={{
              marginRight: isRight ? 12 : undefined,
              marginLeft: !isRight ? 12 : undefined,
            }}
          >
            <VerticalIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={`Move feedback to ${verticalLabels[horizontalTarget]}`}
            onPointerEnter={showHandles}
            onPointerLeave={scheduleHide}
            onClick={(e) => {
              e.stopPropagation()
              fab.setCorner(horizontalTarget)
            }}
            className={cn(
              "absolute w-6 h-6 rounded-full flex items-center justify-center z-10",
              anchorEdge,
              horizontalOffset,
              "bg-background text-foreground",
              "border-2 border-foreground transition-opacity duration-200",
              handlesVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            )}
            style={{
              marginBottom: isBottom ? 12 : undefined,
              marginTop: !isBottom ? 12 : undefined,
            }}
          >
            <HorizontalIcon className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      <button
        aria-label={tw("submitFeedback")}
        aria-haspopup="dialog"
        className={cn(
          "absolute w-12 h-12 rounded-full flex items-center justify-center z-10",
          anchorEdge,
          "bg-background text-foreground border border-border shadow-sm",
          "pointer-events-auto",
          fab.isDragging && "opacity-70",
        )}
        style={
          fab.dragPos
            ? { position: "fixed", left: fab.dragPos.x - 24, top: fab.dragPos.y - 24, bottom: "auto", right: "auto" }
            : undefined
        }
        {...fab.dragHandlers}
        onPointerEnter={showHandles}
        onPointerLeave={scheduleHide}
        onClick={handleClick}
        title={tw("submitFeedback")}
      >
        <MessageSquareHeart className="h-5 w-5" />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1">
            {badge}
          </span>
        )}
      </button>
    </>,
    host,
  )
}
